import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useVideoPlayerStore } from "../../utils/store";
import { makeFullscreen } from "../../utils/makeFullscreen";
import { motion } from "framer-motion";
import { useKeyPressEvent, useMouse } from "react-use";
import { Header } from "./Header";
import { captureException } from "@sentry/react";
import { playerActivationStore } from "../../utils/firebase";
import { useAnalytics } from "../hooks/useAnalytics";
import { createWatchCounter } from "../watchTime";

export function FullScreenPlayer() {
	const videoElRef = useRef<HTMLVideoElement>(null);
	const [isForcedFullscreen, setIsForcedFullscreen] = useState(false);
	const [videoError, setVideoError] = useState(false);
	const errorSkipTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const videoPlayer = useVideoPlayerStore();

	const currentVideo = videoPlayer.videos[0];
	const isSingleVideo = videoPlayer.videos.length === 1;

	const analytics = useAnalytics();
	const watchCounterRef = useRef(createWatchCounter());
	// The screensaver loop is not learning time, so it is never reported.
	const watchedVideoId = videoPlayer.isSplash ? undefined : currentVideo?.id;
	const watchedVideoTitle = currentVideo?.title ?? "";

	// Reports once per play: when the video ends, the next one starts or the
	// player closes.
	useEffect(() => {
		if (!watchedVideoId) return;
		const counter = watchCounterRef.current;
		counter.take();
		return () => {
			const seconds = counter.take();
			if (seconds > 0) {
				analytics
					.watched({
						playerId: playerActivationStore.activationPassword!,
						videoId: watchedVideoId,
						videoName: watchedVideoTitle,
						seconds,
					})
					.catch(() => {});
			}
		};
	}, [analytics, watchedVideoId, watchedVideoTitle]);

	const mousePos = useMouse({
		current: document.body as unknown as Element,
	});

	useKeyPressEvent("Escape", () => {
		videoPlayer.stop();
	});

	useLayoutEffect(() => {
		if (videoElRef.current) {
			if (videoPlayer.isSplash) {
				makeFullscreen(videoElRef.current, async () => {
					setIsForcedFullscreen(true);
				});
			}
		}
	}, [videoElRef.current, videoPlayer.isSplash]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.currentTime = 0;
		}
	}, [currentVideo?.originalUrl, currentVideo?.url]);

	useEffect(() => {
		return () => {
			clearTimeout(errorSkipTimeoutRef.current);
		};
	}, []);

	if (!videoPlayer.isPlaying) {
		return <></>;
	}

	const videoRendered = (
		<>
			{videoPlayer.isSplash ? null : <Header />}
			<div
				className="w-full flex-grow"
				style={{
					flex: "0 1 100%",
					height: "50%",
					display: "flex",
					justifyContent: "flex-start",
				}}
			>
				<video
					className="h-full w-auto"
					src={currentVideo?.url || currentVideo?.originalUrl}
					autoPlay
					style={{
						flex: "0 1 100%",
					}}
					ref={videoElRef}
					muted={videoPlayer.isSplash}
					controls={!videoPlayer.isSplash}
					loop={videoPlayer.isLoop && isSingleVideo}
					onTimeUpdate={(e) => {
						if (!videoPlayer.isSplash) {
							watchCounterRef.current.tick(e.currentTarget.currentTime);
						}
					}}
					onEnded={() => {
						videoPlayer.videoFinished();
					}}
					onError={() => {
						captureException(
							new Error(
								`Erro ao carregar vídeo: ${currentVideo?.url || currentVideo?.originalUrl}`,
							),
						);
						setVideoError(true);
						// Skip to next video after 3 seconds. Single timer:
						// consecutive errors must not stack extra skips
						clearTimeout(errorSkipTimeoutRef.current);
						errorSkipTimeoutRef.current = setTimeout(() => {
							setVideoError(false);
							videoPlayer.videoFinished();
						}, 3000);
					}}
				/>
				{videoError && (
					<div className="absolute inset-0 flex items-center justify-center bg-black/80">
						<div className="text-center text-white">
							<p className="text-xl">Erro ao carregar vídeo</p>
							<p className="text-sm opacity-70">Carregando próximo...</p>
						</div>
					</div>
				)}
			</div>
		</>
	);

	if (videoPlayer.isSplash && !isForcedFullscreen) {
		return videoRendered;
	}

	if (!videoPlayer.isSplash) {
		return (
			<motion.div
				className="w-screen h-screen object-contain fixed z-10 bg-black flex flex-col"
				initial={{
					scale: 0.1,
					origin: "center",
					top: mousePos.docY,
					left: mousePos.docX,
					translateX: "-50%",
					translateY: "-50%",
				}}
				transition={{ duration: 0.6, ease: "easeOut" }}
				animate={{ scale: 1, top: 0, left: 0, translateX: 0, translateY: 0 }}
				exit={{ opacity: 0, scale: 0 }}
			>
				{videoRendered}
			</motion.div>
		);
	}

	return (
		<motion.div
			className="w-screen h-screen object-contain fixed z-10 bg-black flex flex-col"
			initial={{ opacity: 0 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
		>
			{videoRendered}
		</motion.div>
	);
}
